import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ChatMessage } from '~/components/chat/chatTypes'
import type { CanvasItem } from '~/components/chat/message'
import { Button } from '~/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { LoadingState } from '~/components/common/LoadingSpinner'
import { Typography } from '~/components/common/Typography'
import { useCurrentConversation } from '~/contexts/CurrentConversationContext'
import {
  ProgressMessageData,
  ProgressMessageTodoItem,
  StreamingMessage
} from '~/contexts/GlobalStreamingContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useUserContext } from '~/contexts/UserContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useChatLifecycle } from '~/hooks/chat/useChatLifecycle'
import { useConversationUuid } from '~/hooks/conversation/useConversationUuid'
import { useStreamingChat } from '~/hooks/useStreamingChat'
import ChatDisplay from '~/layouts/ChatDisplay'
import { TaskStatuses } from '~/constants/task-status'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  commandTypeFromJSON
} from '~/protos/agents'
import type { CanvasContextInfo } from '~/services/chatService'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { getConversationDisplayTitle } from '~/utils/taskTitles'

export function getScratchCanvasItemFromAgentTrace(agentTrace: unknown): CanvasItem | undefined {
  if (!agentTrace) return undefined

  try {
    const trace = typeof agentTrace === 'string' ? JSON.parse(agentTrace) : agentTrace
    if (!trace || typeof trace !== 'object') return undefined

    const s3Path = (trace as Record<string, unknown>).s3_scratch_dir_tar_gz_path
    if (typeof s3Path !== 'string' || !s3Path.trim()) return undefined

    return {
      canvasType: 'CANVAS_TYPE_S3_FILE',
      content: '',
      s3FilePath: s3Path.trim(),
      title: 'Scratch working directory',
      base64EncodedImage: '',
      editable: false
    }
  } catch {
    return undefined
  }
}

import {
  convertApiMessagesToStreamingMessages,
  createOptimisticUserMessage,
  parseMessageId
} from './conversationChatUtils'

export { parseMessageId }

const getStreamingMsgId = (m: StreamingMessage) => m.id

function getMessageCreatedAtMs(message: StreamingMessage): number | null {
  const createdAtMs = Date.parse(message.createdTime || '')
  return Number.isFinite(createdAtMs) ? createdAtMs : null
}

function getSelectionWidgetId(message: StreamingMessage): string | null {
  if (message.role !== 'assistant' || message.type !== 'selection_widget') return null

  const widgetId = message.selectionWidget?.widgetId?.trim()
  return widgetId || null
}

function findPreviousUserMessage(
  messages: StreamingMessage[],
  startIndex: number
): StreamingMessage | null {
  for (let index = startIndex - 1; index >= 0; index -= 1) {
    const message = messages[index]
    if (message.role === 'user' && message.type === 'text') {
      return message
    }
  }

  return null
}

function findPersistedUserMatchIndex(
  persistedMessages: StreamingMessage[],
  streamingUserMessage: StreamingMessage | null
): number {
  if (
    !streamingUserMessage ||
    streamingUserMessage.role !== 'user' ||
    streamingUserMessage.type !== 'text'
  ) {
    return -1
  }

  const streamingCreatedAtMs = getMessageCreatedAtMs(streamingUserMessage)
  if (streamingCreatedAtMs === null) return -1

  return persistedMessages.findIndex(persistedMessage => {
    if (
      persistedMessage.role !== 'user' ||
      persistedMessage.type !== 'text' ||
      persistedMessage.content !== streamingUserMessage.content
    ) {
      return false
    }

    const persistedCreatedAtMs = getMessageCreatedAtMs(persistedMessage)
    if (persistedCreatedAtMs === null) return false

    return persistedCreatedAtMs >= streamingCreatedAtMs - 1_000
  })
}

function persistedTurnHasAssistantText(
  persistedMessages: StreamingMessage[],
  persistedUserIndex: number,
  assistantContent: string
): boolean {
  if (persistedUserIndex < 0) return false

  for (let index = persistedUserIndex + 1; index < persistedMessages.length; index += 1) {
    const message = persistedMessages[index]
    if (message.role === 'user') return false
    if (
      message.role === 'assistant' &&
      message.type === 'text' &&
      message.content === assistantContent
    ) {
      return true
    }
  }

  return false
}

function hasPersistedTurnMatchForStreamingAssistant(
  persistedMessages: StreamingMessage[],
  streamingMessages: StreamingMessage[],
  streamingAssistantIndex: number
): boolean {
  const streamingAssistant = streamingMessages[streamingAssistantIndex]
  if (
    !streamingAssistant ||
    streamingAssistant.role !== 'assistant' ||
    streamingAssistant.type !== 'text' ||
    streamingAssistant.content.trim().length === 0
  ) {
    return false
  }

  const streamingUserMessage = findPreviousUserMessage(streamingMessages, streamingAssistantIndex)
  const persistedUserIndex = findPersistedUserMatchIndex(persistedMessages, streamingUserMessage)

  return persistedTurnHasAssistantText(
    persistedMessages,
    persistedUserIndex,
    streamingAssistant.content
  )
}

function isPersistedAssistantMatchForStreamingAssistant(
  persistedMessages: StreamingMessage[],
  persistedAssistantIndex: number,
  streamingMessages: StreamingMessage[],
  streamingAssistantIndex: number
): boolean {
  const persistedAssistant = persistedMessages[persistedAssistantIndex]
  const streamingAssistant = streamingMessages[streamingAssistantIndex]
  if (
    !persistedAssistant ||
    !streamingAssistant ||
    persistedAssistant.role !== 'assistant' ||
    persistedAssistant.type !== 'text' ||
    streamingAssistant.role !== 'assistant' ||
    streamingAssistant.type !== 'text' ||
    persistedAssistant.content !== streamingAssistant.content
  ) {
    return false
  }

  const streamingUserMessage = findPreviousUserMessage(streamingMessages, streamingAssistantIndex)
  const persistedUserIndex = findPersistedUserMatchIndex(persistedMessages, streamingUserMessage)
  if (persistedUserIndex < 0 || persistedAssistantIndex <= persistedUserIndex) return false

  for (let index = persistedUserIndex + 1; index < persistedAssistantIndex; index += 1) {
    if (persistedMessages[index].role === 'user') return false
  }

  return true
}

function getProgressGroupId(prefix: string, progressMessages: ProgressMessageData[]): string {
  const identity = progressMessages
    .map(message => message.msgHash || message.title || message.createdTsNs || 'progress')
    .join('-')
  return `${prefix}-${identity || 'progress'}`
}

const LANDING_CODER_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.GENERATE_SIMPLE_CODE_PR,
  CommandType.MULE_VERSION_UPGRADE
])

const LANDING_DATAWEAVE_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION,
  CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE
])

const LANDING_CHAT_BLOCKING_COMMANDS: ReadonlySet<CommandType> = new Set([
  ...LANDING_CODER_COMMANDS,
  ...LANDING_DATAWEAVE_COMMANDS,
  CommandType.GENERATE_DOCUMENTATION
])

function isBlockingCoderTask(
  status: AgentTaskStatus | undefined,
  commandType: CommandType
): boolean {
  if (!status || !LANDING_CODER_COMMANDS.has(commandType)) return false
  return TaskStatuses[status]?.group === 'IN_PROGRESS'
}

function isBlockingLandingTask(
  status: AgentTaskStatus | undefined,
  commandType: CommandType
): boolean {
  if (!status || !LANDING_CHAT_BLOCKING_COMMANDS.has(commandType)) return false
  return TaskStatuses[status]?.group === 'IN_PROGRESS'
}

function isCoderTaskReadyForFeedback(
  status: AgentTaskStatus | undefined,
  commandType: CommandType
): boolean {
  if (!status || !LANDING_CODER_COMMANDS.has(commandType)) return false
  return status === AgentTaskStatus.DIFF_GENERATED
}

function isDataWeaveTaskReadyForFeedback(
  status: AgentTaskStatus | undefined,
  commandType: CommandType
): boolean {
  if (!status || !LANDING_DATAWEAVE_COMMANDS.has(commandType)) return false
  return status === AgentTaskStatus.DOCUMENT_GENERATED
}

export function ChatDisplayController({
  onConversationCreated
}: { onConversationCreated?: () => void } = {}) {
  const navigate = useNavigate()
  const optimisticMessageSeqRef = useRef(0)
  const conversationUuid = useConversationUuid()
  const { currentWorkspace } = useWorkspaceContext()
  const { user } = useUserContext()
  const { openPlan } = useModalContext()
  const workspaceUuid = currentWorkspace?.uuid
  const {
    conversation,
    inline_messages: conversationInlineMessages,
    isLoading: isConversationLoading,
    showLoadingIndicator,
    refetch: refetchMessages,
    dataHidden,
    isFreeLicense,
    creditsUsed,
    creditsLimit
  } = useCurrentConversation()
  const {
    messages: streamingMessages,
    isStreaming,
    sendMessage,
    startWorkspaceChat
  } = useStreamingChat(conversationUuid)

  const isWorkspaceHome = !conversationUuid
  const isProcessing = conversation?.is_processing ?? false
  const hasBlockingCoderTask = useMemo(
    () =>
      conversation?.tasks?.some(task =>
        isBlockingCoderTask(
          agentTaskStatusFromJSON(task.status),
          commandTypeFromJSON(task.cmd_type)
        )
      ) ?? false,
    [conversation?.tasks]
  )
  const hasBlockingLandingTask = useMemo(
    () =>
      conversation?.tasks?.some(task =>
        isBlockingLandingTask(
          agentTaskStatusFromJSON(task.status),
          commandTypeFromJSON(task.cmd_type)
        )
      ) ?? false,
    [conversation?.tasks]
  )
  const readyCoderTaskIds = useMemo(() => {
    return (
      conversation?.tasks
        ?.filter(task =>
          isCoderTaskReadyForFeedback(
            agentTaskStatusFromJSON(task.status),
            commandTypeFromJSON(task.cmd_type)
          )
        )
        .sort((a, b) => b.task_id - a.task_id)
        .map(task => task.task_id) ?? []
    )
  }, [conversation?.tasks])
  const readyDataWeaveTaskIds = useMemo(() => {
    return (
      conversation?.tasks
        ?.filter(task =>
          isDataWeaveTaskReadyForFeedback(
            agentTaskStatusFromJSON(task.status),
            commandTypeFromJSON(task.cmd_type)
          )
        )
        .sort((a, b) => b.task_id - a.task_id)
        .map(task => task.task_id) ?? []
    )
  }, [conversation?.tasks])
  const readyCoderTaskId = readyCoderTaskIds.length === 1 ? readyCoderTaskIds[0] : undefined
  const readyDataWeaveTaskId =
    readyDataWeaveTaskIds.length === 1 ? readyDataWeaveTaskIds[0] : undefined
  const readyFeedbackTaskCount = readyCoderTaskIds.length + readyDataWeaveTaskIds.length
  const inputPlaceholder = useMemo(() => {
    if (hasBlockingCoderTask) {
      return 'Coder is running. You can continue when changes are ready...'
    }
    if (hasBlockingLandingTask) {
      return 'Task is running. You can continue when results are ready...'
    }
    if (readyCoderTaskId && readyFeedbackTaskCount === 1) {
      return `Request code changes for task #${readyCoderTaskId} or ask a question...`
    }
    if (readyDataWeaveTaskId && readyFeedbackTaskCount === 1) {
      return `Request DataWeave changes for task #${readyDataWeaveTaskId} or ask a question...`
    }
    if (readyFeedbackTaskCount > 1) {
      return 'Ask a question or mention the task id for task feedback...'
    }
    return 'Ask a question...'
  }, [
    hasBlockingCoderTask,
    hasBlockingLandingTask,
    readyCoderTaskId,
    readyDataWeaveTaskId,
    readyFeedbackTaskCount
  ])
  const scratchCanvasItem = useMemo(
    () => getScratchCanvasItemFromAgentTrace(conversation?.conversation_metadata?.agent_trace),
    [conversation?.conversation_metadata?.agent_trace]
  )

  // ── Shared chat lifecycle ──────────────────────────────────────────
  const {
    optimisticMessages,
    addOptimistic,
    removeOptimistic,
    hasStreamedInSession,
    pendingStreamRefetch,
    onStreamingDoneRef,
    initialMessage,
    initialMessageIsSpeechInput,
    consumeInitialMessage
  } = useChatLifecycle<StreamingMessage>({
    conversationUuid,
    isStreaming,
    isProcessing,
    isLoading: isConversationLoading,
    refetch: refetchMessages,
    getOptimisticId: getStreamingMsgId
  })

  // feedbackOverrides are specific to this chat container's message model.
  const [feedbackOverrides, setFeedbackOverrides] = useState<
    Record<
      string,
      { feedback?: 'positive' | 'negative'; feedbackReason?: string; otherReason?: string }
    >
  >({})
  useEffect(() => {
    setFeedbackOverrides({})
  }, [conversationUuid])
  onStreamingDoneRef.current = () => setFeedbackOverrides({})

  // Reconstruct streaming view from server-side snapshot (for refreshed clients).
  // The backend persists a full snapshot (text, tasks, progress, canvas) every 2s so
  // any client can reconstruct the current view without a live SSE connection.
  // Suppressed while pendingStreamRefetch is true — we already have live streaming messages.
  const snapshotMessages = useMemo((): StreamingMessage[] => {
    if (isStreaming || pendingStreamRefetch || !isProcessing) return []

    const snapshot = conversation?.conversation_metadata?.streaming_snapshot
    if (!snapshot) return []

    const messages: StreamingMessage[] = []

    // Progress messages
    if (snapshot.progress_messages?.length) {
      for (const [idx, pm] of snapshot.progress_messages.entries()) {
        messages.push({
          id: `snapshot-progress-${idx}`,
          role: 'assistant',
          type: 'progress_message',
          content: pm.title || '',
          progressMessage: {
            taskStatus: pm.taskStatus || '',
            messageType: pm.messageType || 'INTERNAL_PROGRESS',
            title: pm.title || '',
            internalMsgs: pm.internalMsgs || [],
            toolOutputs: pm.toolOutputs || [],
            checkpointMsg: pm.checkpointMsg || '',
            msgHash: pm.msgHash || `snapshot-${idx}`,
            createdTsNs: pm.createdTsNs || 0,
            preview: pm.preview,
            todoList: pm.todoList?.map(t => ({
              todo: t.todo,
              status: t.status as ProgressMessageTodoItem['status']
            }))
          }
        })
      }
    }

    // Task messages
    if (snapshot.tasks?.length) {
      for (const task of snapshot.tasks) {
        messages.push({
          id: `snapshot-task-${task.task_id}`,
          role: 'assistant',
          type: 'task',
          content: '',
          taskId: task.task_id,
          taskTitle: task.title
        })
      }
    }

    // Text content
    if (snapshot.text_content) {
      messages.push({
        id: 'snapshot-text',
        role: 'assistant',
        type: 'text',
        content: snapshot.text_content
      })
    }

    // Canvas items
    if (snapshot.canvas_items?.length) {
      messages.push({
        id: 'snapshot-canvas',
        role: 'assistant',
        type: 'canvas',
        content: '',
        canvasItems: snapshot.canvas_items.map(c => ({
          canvasType: c.canvas_type as CanvasItem['canvasType'],
          content: c.content,
          title: c.title,
          s3FilePath: c.s3_file_path,
          editable: c.editable,
          base64EncodedImage: ''
        }))
      })
    }

    if (snapshot.selection_widgets?.length) {
      for (const [idx, widget] of snapshot.selection_widgets.entries()) {
        messages.push({
          id: `snapshot-selection-widget-${idx}`,
          role: 'assistant',
          type: 'selection_widget',
          content: '',
          selectionWidget: widget
        })
      }
    }

    return messages
  }, [
    isStreaming,
    pendingStreamRefetch,
    isProcessing,
    conversation?.conversation_metadata?.streaming_snapshot
  ])

  // ── Derived messages (single source of truth) ───────────────────────
  // Step 1: Convert API messages (only re-runs when inline_messages change)
  const persistedMessages = useMemo(
    () => convertApiMessagesToStreamingMessages(conversationInlineMessages),
    [conversationInlineMessages]
  )

  // Step 2: Compose final message list — priority-ordered merge with Set-based
  // user-message dedup.  Sources are processed highest-priority first; a user
  // message is only shown from the first source that provides it.
  //
  // Priority: Persisted > Optimistic > Streaming > Snapshot
  const messages = useMemo(() => {
    const hasFeedback = Object.keys(feedbackOverrides).length > 0
    const latestStreamingAssistantTextIndex = (() => {
      for (let index = streamingMessages.length - 1; index >= 0; index -= 1) {
        const msg = streamingMessages[index]
        if (
          msg.role === 'assistant' &&
          msg.type === 'text' &&
          !!msg.textDeltas?.length &&
          msg.content.trim().length > 0
        ) {
          return index
        }
      }
      return -1
    })()
    const latestStreamingAssistantText =
      latestStreamingAssistantTextIndex >= 0
        ? streamingMessages[latestStreamingAssistantTextIndex]
        : undefined
    const hasUnpersistedStreamingAssistantText = streamingMessages.some(
      (msg, index) =>
        msg.role === 'assistant' &&
        msg.type === 'text' &&
        !!msg.textDeltas?.length &&
        msg.content.trim().length > 0 &&
        !hasPersistedTurnMatchForStreamingAssistant(persistedMessages, streamingMessages, index)
    )
    const activeStreaming =
      isStreaming || pendingStreamRefetch || hasUnpersistedStreamingAssistantText
        ? streamingMessages
        : []

    const streamingUserContentToSkip = new Set<string>()
    const seenSelectionWidgetIds = new Set<string>()
    const result: StreamingMessage[] = []
    const appendMessage = (
      message: StreamingMessage,
      options: { skipIfSelectionWidgetSeen?: boolean } = {}
    ) => {
      const selectionWidgetId = getSelectionWidgetId(message)
      if (selectionWidgetId) {
        if (options.skipIfSelectionWidgetSeen && seenSelectionWidgetIds.has(selectionWidgetId)) {
          return
        }
        seenSelectionWidgetIds.add(selectionWidgetId)
      }
      result.push(message)
    }

    // Streaming runs in GlobalStreamingContext (per-conversation, persists
    // across mounts). `sendMessageInternal` prepends a `userEchoMessage`
    // with `createdTime: new Date().toISOString()` to the stream, and that
    // echo lives for the entire stream. The local optimistic message that
    // normally shadows it is wiped whenever ConversationPage remounts via
    // `key={conversation?.uuid}` (e.g. user navigates to a sibling
    // conversation and back to a still-streaming one). Without this dedup,
    // the merge below renders both the persisted user row AND the live
    // streaming echo on the remounted view.
    //
    // Mirrors `hasPersistedMatchForOptimistic`: same content + close
    // timestamp counts as a match (echo suppressed). An older persisted
    // match is treated as a distinct prior turn (so the existing "user
    // sent the same text twice" tests still pass — see the "shows a
    // repeated user message" / "keeps streaming user echo visible when
    // persisted timestamps are missing" tests).
    const hasPersistedMatchForStreamingUser = (streamingMsg: StreamingMessage): boolean => {
      if (streamingMsg.role !== 'user') return false
      const streamingCreatedAt = Date.parse(streamingMsg.createdTime || '')
      if (!Number.isFinite(streamingCreatedAt)) return false
      return persistedMessages.some(persistedMsg => {
        if (persistedMsg.role !== 'user' || persistedMsg.content !== streamingMsg.content) {
          return false
        }
        const persistedCreatedAt = Date.parse(persistedMsg.createdTime || '')
        if (!Number.isFinite(persistedCreatedAt)) return false
        return persistedCreatedAt >= streamingCreatedAt - 1_000
      })
    }

    const hasPersistedMatchForOptimistic = (optimisticMsg: StreamingMessage): boolean => {
      if (optimisticMsg.role !== 'user') return false

      const optimisticCreatedAt = Date.parse(optimisticMsg.createdTime || '')
      return persistedMessages.some(persistedMsg => {
        if (persistedMsg.role !== 'user' || persistedMsg.content !== optimisticMsg.content) {
          return false
        }

        const persistedCreatedAt = Date.parse(persistedMsg.createdTime || '')
        if (Number.isFinite(optimisticCreatedAt) && Number.isFinite(persistedCreatedAt)) {
          return persistedCreatedAt >= optimisticCreatedAt - 1_000
        }

        // If timestamps are unavailable, keep the optimistic message visible.
        // A duplicate current user row is less harmful than hiding the turn
        // because an older persisted message happened to have the same text.
        return false
      })
    }

    const unsyncedOptimisticMessages: StreamingMessage[] = []
    for (const msg of optimisticMessages) {
      if (hasPersistedMatchForOptimistic(msg)) {
        streamingUserContentToSkip.add(msg.content)
        continue
      }
      unsyncedOptimisticMessages.push(msg)
    }

    const firstUnsyncedOptimisticCreatedAt = unsyncedOptimisticMessages
      .map(msg => Date.parse(msg.createdTime || ''))
      .find(timestamp => Number.isFinite(timestamp))

    let optimisticInserted = false
    const insertOptimisticMessages = () => {
      if (optimisticInserted) return
      for (const msg of unsyncedOptimisticMessages) {
        if (msg.role === 'user') streamingUserContentToSkip.add(msg.content)
        appendMessage(msg)
      }
      optimisticInserted = true
    }

    // 1. Persisted — authoritative, has full metadata (userName, createdTime, feedback).
    // If an assistant response persists before its matching user row has arrived,
    // keep the optimistic user message above it to avoid a visible reordering jump.
    for (const msg of persistedMessages) {
      const msgCreatedAt = Date.parse(msg.createdTime || '')
      if (
        !optimisticInserted &&
        unsyncedOptimisticMessages.length > 0 &&
        msg.role === 'assistant' &&
        typeof firstUnsyncedOptimisticCreatedAt === 'number' &&
        Number.isFinite(firstUnsyncedOptimisticCreatedAt) &&
        Number.isFinite(msgCreatedAt) &&
        msgCreatedAt >= firstUnsyncedOptimisticCreatedAt
      ) {
        insertOptimisticMessages()
      }

      const override = hasFeedback ? feedbackOverrides[msg.id] : undefined
      const matchesLatestStreamingAssistant =
        msg.role === 'assistant' &&
        msg.type === 'text' &&
        latestStreamingAssistantTextIndex >= 0 &&
        isPersistedAssistantMatchForStreamingAssistant(
          persistedMessages,
          persistedMessages.indexOf(msg),
          streamingMessages,
          latestStreamingAssistantTextIndex
        )

      if (pendingStreamRefetch && matchesLatestStreamingAssistant) {
        continue
      }

      const nextMessage =
        !isStreaming &&
        !pendingStreamRefetch &&
        matchesLatestStreamingAssistant &&
        latestStreamingAssistantText
          ? {
              ...msg,
              id: latestStreamingAssistantText.id,
              textDeltas: latestStreamingAssistantText.textDeltas,
              continueStreamingAnimation: true
            }
          : msg

      appendMessage(override ? { ...nextMessage, ...override } : nextMessage)
    }

    // 2. Optimistic — fills the gap before persisted data arrives
    if (!optimisticInserted) {
      insertOptimisticMessages()
    }

    // 3. Streaming — user echo skipped if already shown; assistant text skipped once persisted
    for (let streamingIndex = 0; streamingIndex < activeStreaming.length; streamingIndex += 1) {
      const msg = activeStreaming[streamingIndex]
      if (msg.role === 'user' && streamingUserContentToSkip.has(msg.content)) continue
      if (msg.role === 'user' && hasPersistedMatchForStreamingUser(msg)) continue
      if (
        msg.role === 'assistant' &&
        msg.type === 'text' &&
        hasPersistedTurnMatchForStreamingAssistant(
          persistedMessages,
          activeStreaming,
          streamingIndex
        ) &&
        !(pendingStreamRefetch && msg.id === latestStreamingAssistantText?.id)
      ) {
        continue
      }
      const nextMessage =
        pendingStreamRefetch && msg.role === 'assistant' && msg.type === 'text'
          ? { ...msg, continueStreamingAnimation: true }
          : msg
      appendMessage(nextMessage, { skipIfSelectionWidgetSeen: true })
    }

    // 4. Snapshot — fallback for refreshed clients (assistant-only content).
    // Conversation metadata can briefly hold the previous turn's snapshot
    // while the next user turn is loading. Selection widgets have stable
    // widgetIds, so lower-priority snapshot copies must not be appended after
    // the next user message once the same widget is already represented by
    // persisted or live streaming data.
    for (const msg of snapshotMessages) {
      appendMessage(msg, { skipIfSelectionWidgetSeen: true })
    }

    return result
  }, [
    persistedMessages,
    optimisticMessages,
    streamingMessages,
    feedbackOverrides,
    snapshotMessages,
    isStreaming,
    pendingStreamRefetch
  ])

  // Update message feedback locally without refetching
  const updateMessageFeedback = useCallback(
    (
      messageId: string,
      feedback: 'positive' | 'negative' | undefined,
      feedbackReason?: string,
      otherReason?: string
    ) => {
      setFeedbackOverrides(prev => ({
        ...prev,
        [messageId]: { feedback, feedbackReason, otherReason }
      }))
    },
    []
  )

  const handleSendMessage = useCallback(
    async (
      content: string,
      executionMode?: string,
      verificationMode?: string,
      contextUpload?: Array<{
        file_upload?: { file_name: string; s3_path: string }
        directory_upload?: { directory_name: string; s3_path: string }
      }>,
      canvasContextInfo?: CanvasContextInfo[],
      isSpeechInput?: boolean
    ) => {
      if (!content.trim() || isStreaming || isProcessing || hasBlockingLandingTask) {
        return
      }

      const messageId = `user-${Date.now()}-${optimisticMessageSeqRef.current++}`
      const userMessage = createOptimisticUserMessage({
        messageId,
        content: content.trim(),
        userName: user?.name,
        contextUpload
      })

      addOptimistic(userMessage)

      const messageContent = content.trim()

      if (isWorkspaceHome) {
        if (!workspaceUuid) return

        try {
          const conversation = await startWorkspaceChat()

          if (conversation?.uuid) {
            onConversationCreated?.()
            const speechInputParam = isSpeechInput ? '&initial_message_is_speech_input=true' : ''
            navigate(
              `/w/${workspaceUuid}/c/${conversation.uuid}?initial_message=${encodeURIComponent(messageContent)}${speechInputParam}`
            )
          }
        } catch {
          removeOptimistic(messageId)
        }
      } else {
        if (!conversationUuid) return

        const sendPromise = isSpeechInput
          ? sendMessage(
              messageContent,
              executionMode,
              verificationMode,
              contextUpload,
              canvasContextInfo,
              true
            )
          : sendMessage(
              messageContent,
              executionMode,
              verificationMode,
              contextUpload,
              canvasContextInfo
            )

        sendPromise.catch(() => {
          removeOptimistic(messageId)
        })
      }
    },
    [
      sendMessage,
      startWorkspaceChat,
      isStreaming,
      isProcessing,
      hasBlockingLandingTask,
      conversationUuid,
      workspaceUuid,
      isWorkspaceHome,
      navigate,
      user?.name,
      onConversationCreated,
      addOptimistic,
      removeOptimistic
    ]
  )

  // Handle initial_message URL parameter (component-specific: reads conversation metadata)
  useEffect(() => {
    if (!initialMessage) return

    const executionMode = 'agent'
    const verificationMode = 'verify'

    let contextUpload:
      | Array<{
          file_upload?: { file_name: string; s3_path: string }
          directory_upload?: { directory_name: string; s3_path: string }
        }>
      | undefined

    const storageKey = `context_upload_${conversationUuid}`
    const storedContextUpload = sessionStorage.getItem(storageKey)
    if (storedContextUpload) {
      try {
        contextUpload = JSON.parse(storedContextUpload)
        sessionStorage.removeItem(storageKey)
      } catch (e) {
        console.error('[ChatDisplayController] Failed to parse context_upload from storage:', e)
      }
    }

    if (initialMessageIsSpeechInput) {
      handleSendMessage(
        initialMessage,
        executionMode,
        verificationMode,
        contextUpload,
        undefined,
        true
      )
    } else {
      handleSendMessage(initialMessage, executionMode, verificationMode, contextUpload)
    }
    consumeInitialMessage()
  }, [
    initialMessage,
    initialMessageIsSpeechInput,
    consumeInitialMessage,
    handleSendMessage,
    conversationUuid
  ])

  const conversationHeader = useMemo(() => {
    if (!conversation) {
      return { title: null, subtitle: null }
    }

    const metadata = conversation.conversation_metadata || {}
    const title = getConversationDisplayTitle(conversation.title, metadata.mode)

    const createTitleNode = (text: string) => (
      <Typography fontFamily='geist' fontSize='titleSm' fontWeight='bold' className='text-gray-900'>
        Conversation: {text}
      </Typography>
    )

    const createSubtitleNode = (text: string) => (
      <Typography
        fontFamily='geist'
        fontSize='bodySm'
        fontWeight='medium'
        className='text-gray-600'
      >
        {text}
      </Typography>
    )

    const RepositoryDropdown = ({ repositories }: { repositories: string[] }) => {
      return (
        <div className='flex items-center gap-2'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='outlined'
                className='group text-xs py-0.5 px-2 hover:bg-tokens-neutral-fill-fill-6 rounded-[16px] hover:border-tokens-neutral-border-2 hover:border border-transparent min-h-0 h-auto flex items-center gap-0'
              >
                {createSubtitleNode(`${repositories.length} Repositories Selected`)}
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='opacity-0 group-hover:opacity-100 transition-opacity'
                >
                  <path
                    d='M9.72387 8.00005L4.86194 12.862L5.80475 13.8048L11.6095 8.00005L5.80475 2.19531L4.86194 3.13812L9.72387 8.00005Z'
                    fill='black'
                    fillOpacity='0.65'
                  />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='start'
              className='bg-white box-border flex flex-col gap-3 items-start justify-start px-4 py-2 rounded-xl shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)] min-w-[300px] max-w-[500px]'
            >
              {repositories.map((repo, index: number) => {
                return (
                  <div
                    key={index}
                    className='box-border flex flex-row h-8 items-center justify-start p-0 rounded-lg shrink-0 w-full'
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='semibold'
                      className='text-gray-900 flex-1 overflow-hidden text-ellipsis whitespace-nowrap'
                    >
                      {repo}
                    </Typography>
                  </div>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }

    const titleNode = createTitleNode(title)
    let subtitleNode: React.ReactNode = null
    const combinedNames: string[] = [
      ...(metadata.repository_branches || []).map(repo =>
        repo.branch
          ? `${extractFileDisplayName(repo.repository)} (${repo.branch})`
          : extractFileDisplayName(repo.repository)
      ),
      ...(metadata.s3_upload_paths || []).map(path => {
        const fileName = path.split('/').pop() || path
        const filenameWithExtension = fileName.split('___').pop()
        const filenameWithoutExtension = filenameWithExtension?.split('.')[0] || fileName
        return filenameWithoutExtension
      })
    ]

    if (combinedNames.length > 1) {
      subtitleNode = <RepositoryDropdown repositories={combinedNames} />
    } else if (combinedNames.length === 1) {
      subtitleNode = createSubtitleNode(combinedNames[0])
    } else {
      subtitleNode = createSubtitleNode('No repository chat')
    }
    return { title: titleNode, subtitle: subtitleNode }
  }, [conversation])

  if (isConversationLoading) {
    // Only show loading text after 500ms delay to avoid flash for fast loads
    if (showLoadingIndicator) {
      return <LoadingState message='Loading...' className='h-screen' />
    }
    // During initial load, show empty container to avoid flash
    return <div className='h-screen' />
  }

  // Credit limit exceeded - show upgrade/purchase message (applies to all users in prepay model)
  if (dataHidden) {
    return (
      <div className='flex flex-col h-full items-center justify-center'>
        <div className='flex flex-col items-center justify-center p-8 text-center max-w-md'>
          <div className='text-on-background dark:text-light10 mb-4'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
              strokeWidth={1.5}
              stroke='currentColor'
              className='w-16 h-16 mx-auto mb-4 text-tokens-neutral-fill-fill-3'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                d='M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z'
              />
            </svg>
          </div>
          <h2 className='text-xl font-semibold text-on-background dark:text-light10 mb-2'>
            Credit Limit Reached
          </h2>
          <p className='text-sm text-tokens-neutral-text-text-1 mb-4'>
            You&apos;ve used {creditsUsed} of {creditsLimit} credits this billing period.
            <br />
            {isFreeLicense
              ? 'Upgrade your plan to continue viewing this conversation.'
              : 'Purchase additional credits or upgrade your plan to continue viewing this conversation.'}
          </p>
          <button
            onClick={openPlan}
            className='px-6 py-3 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors'
          >
            {isFreeLicense ? 'Upgrade Plan' : 'Purchase Credits'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-full min-h-0'>
      {/* min-h-0 is required on this flex child so ChatDisplay's internal
          overflow-auto scroll container can establish a bounded height and
          actually scroll (otherwise the chain grows to content height and an
          ancestor scrolls instead, breaking pagination + the scroll pin). */}
      <div className='flex-1 min-h-0'>
        <ChatDisplay
          messages={(() => {
            // Group progress messages into clusters
            // Each cluster contains all progress messages before an assistant response
            const result: ChatMessage[] = []
            let currentProgressMessages: ProgressMessageData[] = []
            const pushProgressGroup = (id: string) => {
              currentProgressMessages.sort((a, b) => {
                const timeA = a.createdTsNs || 0
                const timeB = b.createdTsNs || 0
                return timeA - timeB
              })

              result.push({
                id,
                role: 'assistant' as const,
                type: 'progress_message' as const,
                progressMessages: currentProgressMessages
              })

              currentProgressMessages = []
            }

            for (const msg of messages) {
              if (msg.type === 'progress_message' && msg.progressMessage) {
                // Collect progress messages into current cluster
                currentProgressMessages.push(msg.progressMessage)
              } else {
                if (msg.role === 'user' && currentProgressMessages.length > 0) {
                  pushProgressGroup(
                    getProgressGroupId(`progress-before-${msg.id}`, currentProgressMessages)
                  )
                }

                // When we encounter a non-progress message, check if it's an assistant response
                // Include canvas in the check - canvas also triggers progress cluster insertion
                const isAssistantResponse =
                  msg.role === 'assistant' &&
                  (msg.type === 'text' ||
                    msg.type === 'task' ||
                    msg.type === 'image_base64' ||
                    msg.type === 'canvas' ||
                    msg.type === 'coder_summary' ||
                    msg.type === 'selection_widget')

                // If this is an assistant response and we have progress messages collected BEFORE it,
                // insert them first (these progress messages belong to THIS assistant response)
                if (isAssistantResponse && currentProgressMessages.length > 0) {
                  pushProgressGroup(`progress-${msg.id}`)
                }

                // Map the message
                const baseMessage = {
                  id: msg.id,
                  role: msg.role
                } as const

                if (msg.type === 'image_base64') {
                  result.push({
                    ...baseMessage,
                    type: 'image_base64',
                    content: msg.content
                  })
                } else if (msg.type === 'task') {
                  result.push({
                    ...baseMessage,
                    type: 'task',
                    taskId: msg.taskId || 0,
                    taskTitle: msg.taskTitle || ''
                  })
                } else if (msg.type === 'progress_message') {
                  // Skip individual progress messages - they're already handled above and grouped
                  continue
                } else if (msg.type === 'canvas' && msg.canvasItems) {
                  // Canvas messages - appear after text due to priority sorting
                  result.push({
                    ...baseMessage,
                    type: 'canvas',
                    canvasItems: msg.canvasItems
                  })
                } else if (msg.type === 'coder_summary') {
                  result.push({
                    ...baseMessage,
                    type: 'coder_summary',
                    content: msg.content,
                    taskId: msg.taskId,
                    taskTitle: msg.taskTitle,
                    createdTime: msg.createdTime
                  })
                } else if (msg.type === 'selection_widget' && msg.selectionWidget) {
                  result.push({
                    ...baseMessage,
                    type: 'selection_widget',
                    widget: msg.selectionWidget,
                    createdTime: msg.createdTime
                  })
                } else {
                  // Text messages
                  result.push({
                    ...baseMessage,
                    type: 'text',
                    content: msg.content,
                    textDeltas: msg.textDeltas,
                    continueStreamingAnimation: msg.continueStreamingAnimation,
                    feedback: msg.feedback,
                    feedbackReason: msg.feedbackReason,
                    otherReason: msg.otherReason,
                    showFeedback: msg.showFeedback ?? false,
                    contextUpload: msg.contextUpload,
                    userName: msg.userName,
                    createdTime: msg.createdTime
                  })
                }
              }
            }

            // Handle any remaining progress messages at the end (before next user message)
            if (currentProgressMessages.length > 0) {
              pushProgressGroup(getProgressGroupId('progress-end', currentProgressMessages))
            }

            return result
          })()}
          conversationTitle={conversationHeader.title}
          conversationSubtitle={conversationHeader.subtitle}
          onSendMessage={handleSendMessage}
          isLoading={
            isStreaming || (isProcessing && !hasStreamedInSession) || hasBlockingLandingTask
          }
          inputPlaceholder={inputPlaceholder}
          onFeedbackUpdate={updateMessageFeedback}
          scratchCanvasItem={scratchCanvasItem}
        />
      </div>
    </div>
  )
}

export default ChatDisplayController
